import TextField from "./WorksheetTextField";
import { gql, useQuery } from "@apollo/client";
import {
  Alert,
  MenuItem,
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import UiSideModal from "components/base/UiSideModal";
import UiButton from "components/base/UiButton";
import { CostRow, money, truckingUnits, TruckingUnit } from "./calculator";
import { libraryCostRow, LibraryItem } from "./library";

const queries = {
  trucking: gql`
    query WorksheetTransferTruckLibrary {
      trucks: getTransferTrucks {
        id
        name
        defaultTonnage
        defaultRateHourly
        defaultRateTon
      }
    }
  `,
  labor: gql`
    query WorksheetCrewLibrary($search: String, $page: Int!) {
      library: getCrews(search: $search, page: $page, first: 20, sort: ASC) {
        data {
          id
          name
          rate
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  equipment: gql`
    query WorksheetEquipmentLibrary($search: String, $page: Int!) {
      library: getEquipmentPieces(search: $search, page: $page, first: 20, sort: ASC) {
        data {
          id
          name
          rate
          chargeType
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  other: gql`
    query WorksheetOtherCostLibrary($search: String, $page: Int!) {
      library: getAdditionalCosts(
        search: $search
        page: $page
        first: 20
        sort: ASC
        feeType: FLAT
      ) {
        data {
          id
          name
          cost
          feeAmount
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
};
export type LibraryCategory = keyof typeof queries;
const titles = {
  trucking: "Add Transfer Truck",
  labor: "Add Crew",
  equipment: "Add Equipment",
  other: "Add Other Cost",
};
export default function WorksheetLibraryDrawer({
  category,
  onClose,
  onAdd,
  disabled,
}: {
  category: LibraryCategory;
  onClose: () => void;
  onAdd: (row: CostRow) => void;
  disabled: boolean;
}) {
  const [truckUnit, setTruckUnit] = useState<TruckingUnit>("Hours");
  const [truckRate, setTruckRate] = useState("");
  const needsRate = category === "trucking" && ["Yards", "Loads"].includes(truckUnit);
  const validRate = /^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(truckRate);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, loading, error, refetch } = useQuery(queries[category], {
    variables: { search: debounced, page },
    fetchPolicy: "network-only",
  });
  const pending = loading || search !== debounced;
  const trucks: LibraryItem[] = (data?.trucks ?? []).filter((item: LibraryItem) =>
    item.name.toLowerCase().includes(debounced.toLowerCase())
  );
  const items: LibraryItem[] =
    category === "trucking" ? trucks.slice((page - 1) * 20, page * 20) : data?.library?.data ?? [];
  const more =
    category === "trucking" ? trucks.length > page * 20 : data?.library?.paginatorInfo.hasMorePages;
  const current = items.find((item) => item.id === selected);
  return (
    <UiSideModal open onClose={onClose} title={titles[category]} width="598px">
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {category === "equipment" && (
          <Alert severity="info">
            Uses the base rate only. Fuel surcharge, utilization, and travel settings are excluded.
          </Alert>
        )}
        {category === "other" && <Typography variant="body2">Flat Fee costs only.</Typography>}
        <TextField
          size="small"
          label="Search library"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setSelected("");
          }}
        />
        {category === "trucking" && (
          <>
            <TextField
              select
              size="small"
              label="Billing unit"
              value={truckUnit}
              onChange={(e) => {
                setTruckUnit(e.target.value as TruckingUnit);
                setTruckRate("");
              }}
            >
              {truckingUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
            {needsRate && (
              <TextField
                size="small"
                label={`Rate per ${truckUnit === "Loads" ? "load" : "cubic yard"}`}
                value={truckRate}
                inputProps={{ inputMode: "decimal" }}
                onChange={(e) => setTruckRate(e.target.value)}
                helperText="Enter a rate; this unit has no saved library rate."
              />
            )}
            <Typography variant="body2">
              Tons are US tons; Yards are cubic yards. Truck capacity is reference only.
            </Typography>
          </>
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={() => refetch()}>
                Retry
              </UiButton>
            }
          >
            {error.message}
          </Alert>
        )}
        {pending ? (
          <CircularProgress size={24} aria-label="Loading library" />
        ) : (
          !error && (
            <>
              {!items.length && <Typography>No matching library items.</Typography>}
              <RadioGroup
                aria-label="Library items"
                value={selected}
                onChange={(_, value) => setSelected(value)}
              >
                {items.map((item) => {
                  const row = libraryCostRow(category, item, truckUnit, truckRate);
                  return (
                    <FormControlLabel
                      key={item.id}
                      value={item.id}
                      disabled={disabled}
                      control={<Radio size="small" />}
                      label={`${item.name} — ${
                        row.rate === "" ? "Enter rate" : money(Number(row.rate) * 100)
                      } / ${row.unit}${
                        category === "trucking"
                          ? ` · Capacity ${item.defaultTonnage ?? 0} US tons`
                          : row.quantity !== "1"
                          ? ` · Quantity ${row.quantity}`
                          : ""
                      }`}
                    />
                  );
                })}
              </RadioGroup>
            </>
          )
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || page === 1}
            onClick={() => {
              setPage(page - 1);
              setSelected("");
            }}
          >
            Previous
          </UiButton>
          <Typography variant="body2">Page {page}</Typography>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || !more}
            onClick={() => {
              setPage(page + 1);
              setSelected("");
            }}
          >
            Next
          </UiButton>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <UiButton
            uiVariant="primary-green"
            disabled={!current || pending || !!error || disabled || (needsRate && !validRate)}
            onClick={() => {
              if (current) {
                onAdd(libraryCostRow(category, current, truckUnit, truckRate));
                onClose();
              }
            }}
          >
            Add
          </UiButton>
        </Box>
      </Box>
    </UiSideModal>
  );
}
